/**
 * Helpers for reducing authored HTML to plain text.
 *
 * Used where rich content has to appear in a text-only context (admin table
 * captions, search matching, aria labels) rather than being rendered.
 */

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
}

/**
 * Strips tags (including the contents of script/style blocks) and decodes the
 * handful of entities that show up in authored copy, collapsing whitespace.
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return ''
  return html
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCharCode(Number(code)))
    .replace(/&[a-z]+;/gi, (entity) => ENTITIES[entity.toLowerCase()] ?? entity)
    .replace(/\s+/g, ' ')
    .trim()
}

/** Plain-text preview of authored HTML, truncated on a word boundary. */
export function htmlPreview(html: string | null | undefined, maxLength = 160): string {
  const text = stripHtml(html)
  if (text.length <= maxLength) return text
  const cut = text.slice(0, maxLength)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}
